import { mapDriverPartyToDriverInfo } from '@/utils/DriverUtils';

export const DRIVER_PARTY_TYPE = 'DriverParty';

export class DriverInformation {
	constructor(data: Partial<DriverInformation>) {
		Object.assign(this, data);
	}

	public driverId = '';
	public firstName = '';
	public middleInitial = '';
	public lastName = '';
	public dateOfBirth = '';
	public licenseNumber = '';
	public licenseState = '';
	public insuredAssetNumber = 0;
	public excludedCode = '';
	public genderCode = '';
	public maritalStatusCode = '';
	public assignedUnit?: string;
	public year?: string;
	public classCode?: string;
	public vehicleDescription?: string;
	public violationPrediction?: {
		orderMvr: string;
		orderDate: string;
	};
	public vin?: string | number;
	public selected?: boolean;
	public motorVehicleReport?: OrderMvrResponseBody;
}

export interface VehicleInformation {
	insuredAssetNumber: number,
	year: string,
	vehicleDescription: string,
	classCode: string,
	vin: string | number
}

export interface DriverParty {
	$type: string;
	address?: DriverAddress[];
	driverId?: string | number;
	firstName?: string;
	middleInitial?: string;
	lastName?: string;
	dateOfBirth?: string;
	licenseNumber?: string;
	licenseState?: string;
	insuredAssetNumber?: string | number;
	exludedCode?: string;
	violationPrediction?: {
		orderMvr: string;
		orderDate: string;
	};
	genderCode?: string;
	maritalStatusCode?: string;
	motorVehicleReport?: OrderMvrResponseBody;
}

// New interfaces for the updated API response structure
export interface NewApiDriverInfo {
	insuranceLineCode: string;
	riskLocation: number;
	riskSubLocation: number;
	product: string;
	driverId: number;
	licenseNumber: string;
	licenseState: string;
	relationToInsured: string;
	age: number;
	licenseDate: string | null;
	exludedCode: string;
	insuredAssetNumber: number;
	violationPrediction: {
		orderMvr: string;
		orderDate: string;
	} | null;
	motorVehicleReport: OrderMvrResponseBody | null;
	id: number;
	address: DriverAddress[];
	phone: string | null;
	email: string | null;
	firstName: string;
	lastName: string;
	fullName: string | null;
	middleInitial: string;
	suffix: string;
	dateOfBirth: string;
	genderCode: string;
	maritalStatusCode: string;
}

export interface NewApiLicenseInfo {
	id: string;
	licenseNumber: string;
	licenseState: string;
	violationPrediction: {
		orderMvr: string;
		orderDate: string;
	} | null;
	motorVehicleReport: OrderMvrResponseBody | null;
}

export interface NewApiDriverResponse {
	driver: NewApiDriverInfo;
	licenseInfo: NewApiLicenseInfo;
}

export interface NewApiInsuredParty {
	clientType: string;
	businessEntity?: string;
	businessDescription?: string;
	id: number;
	address: DriverAddress[];
	phone: string | null;
	email: string | null;
	firstName: string;
	lastName: string;
	fullName: string;
	middleInitial: string;
	suffix: string;
	dateOfBirth: string | null;
	genderCode: string | null;
	maritalStatusCode: string | null;
}

export interface NewApiAutoInsuredAsset {
	$type: string;
	vin: string;
	make: string | null;
	model: string | null;
	statedAmount: number;
	year: number;
	sequenceNumber: number;
	vehicleDescription: string;
	leasedVehicleCode: string;
	hasSafetyGlassCoverage: boolean;
	vehicleTypeCode: string;
	ageGroup: string;
	radiusOfOperation: string;
	vehicleUse: string;
	size: string;
	territoryCode: string;
	totalPremium: number;
	id: number;
	address: unknown[];
	coverages: unknown[];
	parties: unknown[];
	classCode: string;
	lineOfBusinessCode: string | null;
	insuranceLineCode: string;
	forms: unknown[];
	insuredAssetNumber: number;
	rateState: string;
	classCodeDescription: string;
	costNew: number;
}

export interface NewApiPolicyResponse {
	insuredAssets: NewApiAutoInsuredAsset[];
	insuredPartys: NewApiInsuredParty[];
	drivers: NewApiDriverResponse[];
}

export class VehicleInformationList {
	public vehicles: VehicleInformation[];

	constructor(data: RawVehicleData[] | NewApiAutoInsuredAsset[]) {
		this.vehicles = data.map(v => ({
			insuredAssetNumber: Number(v.insuredAssetNumber ?? 0),
			year: String(v.year ?? ''),
			vehicleDescription: v.vehicleDescription ?? '',
			classCode: v.classCode ?? '',
			vin: v.vin ?? ''
		}));
	}
}

export class DriverInformationList {
	public drivers: DriverInformation[];

	constructor(data: DriverParty[] | NewApiDriverInfo[]) {
		// Handle both old and new API response formats
		if (data.length > 0 && '$type' in data[0]) {
			// Old format with $type
			this.drivers = (data as DriverParty[])
				.filter(item => item.$type === DRIVER_PARTY_TYPE)
				.map(mapDriverPartyToDriverInfo);
		} else {
			// New format without $type
			this.drivers = (data as NewApiDriverInfo[]).map(driver => 
				new DriverInformation({
					driverId: String(driver.driverId),
					firstName: driver.firstName,
					middleInitial: driver.middleInitial,
					lastName: driver.lastName,
					dateOfBirth: driver.dateOfBirth,
					licenseNumber: driver.licenseNumber,
					licenseState: driver.licenseState,
					insuredAssetNumber: driver.insuredAssetNumber,
					excludedCode: driver.exludedCode,
					genderCode: driver.genderCode,
					maritalStatusCode: driver.maritalStatusCode,
					violationPrediction: driver.violationPrediction,
					motorVehicleReport: driver.motorVehicleReport ?? undefined
				})
			);
		}
	}
}

export interface PolicyResponseData {
	parties: DriverParty[];
	insuredAssets: VehicleInformation[];
}

export interface DriverValidationError {
  driverId: string;
  messages: string[];
}

// Extend DriverInformation for table display with optional MVR properties
export interface DriverTableItem extends DriverInformation {
	// MVR properties that might be added dynamically
	status?: string;
	dateTimeOrdered?: string;
	dateTimeCompleted?: string;
	orderId?: string;
	licenceStatus?: string;
	productProcessingStatusDescription?: string;
	recordType?: string;
	mvrActivity?: MvrActivity;
	alertMessages?: AlertMessages;
}

// Proper type-safe extension for drivers with MVR data
export interface DriverInformationWithMvr extends DriverInformation {
	productProcessingStatusDescription?: string;
	dateTimeCompleted?: string;
	orderId?: string;
	licenceStatus?: string;
	mvrActivity?: MvrActivity;
	alertMessages?: AlertMessages;
	hasMvrData: boolean; // Explicit flag for type safety
}

// Updated list type for drivers with MVR data
export interface DriverInformationListWithMvr {
	drivers: DriverInformationWithMvr[];
}

// Used to populate the request body for driver details
export interface DriverDetailRequest {
	driverId: string;
	license: {
		number: string;
		state: string;
	};
	name: {
		first: string;
		last: string;
		middle?: string;
	};
	address: {
		street: string;
		zipCode: string;
	};
	biometrics: {
		birthDate: string; // ISO date string
		gender?: string;
		maritalStatus?: string;
	};
	policy?: {
		numberOfDrivers?: number;
		youngestDriverAge?: number;
		collisionDeductible?: number;
		priorInsuranceIndicator?: string;
		numberOfChargeableAccidents?: number;
	};
	vehicle?: {
		vin?: string;
		isoSymbol?: number;
		usage?: string;
	};
}

export interface DriverDetailRequestBody {
	persist?: boolean;
	forceMvr?: boolean;
	syncLicenseRequests: DriverDetailRequest[];
}

export interface DriverAddress {
	$type?: string;
	id?: number;
	addressLine?: string;
	extendedAddressLine?: string;
	city?: string;
	state?: string;
	zipCode?: string;
	county?: string;
	country?: string;
}

// Models for the response from the violation order API
export interface MiscellaneousInfo {
	label: string;
	text: string;
}

export interface Violation {
	type: string;
	stateCode?: string;
	stateText?: string;
	nationalCode: string;
	nationalText: string;
	customerCode: string;
	codeClass: string;
	violationDate: string;
	recordType?: string;
	recordDescription?: string;
	caseStatusIndicator?: string;
	dmvPoints?: string;
	stateViolationCode?: string;
	descriptions?: string[];
	fullSVC?: string;
	svcDescription?: string;
	convictionDate?: string;
	convictionState?: string;
	points?: number;
	speedLimit?: number;
	speedDriven?: number;
	speedAmountOverLimit?: number;
	convictionCategoryText?: string;
	miscellaneousInfos?: MiscellaneousInfo[];
	standardViolation?: {
		fullSVC?: string;
		primarySVC?: string;
		description?: string;
		pointsAssigned?: string;
		violationClassification?: string;
		convictionDate?: string;
	};
	primaryDate?: string;
	normalizedConvictionDate?: string;
}

export interface Action {
	type: string;
	stateCode?: string;
	stateText?: string;
	nationalCode: string;
	nationalText: string;
	customerCode: string;
	codeClass: string;
	recordType?: string;
	recordDescription?: string;
	caseStatusIndicator?: string;
	convictionDate?: string;
	actionDate: string;
	effectiveDate?: string;
	stateViolationCode?: string;
	descriptions?: string[];
	reinstatementDate?: string;
	eligibleDate?: string;
	convictionState?: string;
	convictionCategoryText?: string;
	miscellaneousInfos?: MiscellaneousInfo[];
	standardViolation?: {
		fullSVC?: string;
		primarySVC?: string;
		description?: string;
		pointsAssigned?: string;
		violationClassification?: string;
		convictionDate?: string;
	};
	primaryDate?: string;
	normalizedConvictionDate?: string;
}

export interface Accident {
	type: string;
	stateCode?: string;
	stateText?: string;
	nationalCode: string;
	nationalText: string;
	customerCode: string;
	codeClass: string;
	recordType?: string;
	recordDescription?: string;
	caseStatusIndicator?: string;
	accidentDate: string;
	convictionDate?: string;
	state?: string;
	convictionCategoryText?: string;
	miscellaneousInfos?: MiscellaneousInfo[];
	standardViolation?: {
		fullSVC?: string;
		primarySVC?: string;
		description?: string;
		pointsAssigned?: string;
		violationClassification?: string;
		convictionDate?: string;
	};
	primaryDate?: string;
	normalizedConvictionDate?: string;
}

// Alert message is the means to delivering security freeze on MVR report
// code is "SF" for security freeze, several other codes exist
export interface AlertMessages {
	code: string;
	message: string;
}


export interface MvrActivity {
	violations: Violation[];
	actions: Action[];
	accidents: Accident[];
}

export interface NonMvrActivity {
	violations: Violation[];
	actions: Action[];
	accidents: Accident[];
}

export interface OrderMvrResponseBody {
	status?: string;
	dateTimeOrdered?: string;
	dateTimeCompleted?: string;
	orderId?: string;
	licenceStatus?: string;
	productProcessingStatusDescription?: string;
	driverInfo: MvrDriverInfo;
	mvrActivity: MvrActivity;
	alertMessages: AlertMessages;
}

// Array type for handling multiple MVR responses
export type OrderMvrResponseList = OrderMvrResponseBody[];

export interface MvrDriverInfo{
	firstName: string;
	lastName: string;
	middleInitial?: string;
	licenseNumber: string;
	licenseState: string;
	dob: string;
	gender?: string;
	dlClass?: string;
	licenceStatus?: string;
}

// Simplified response for the violation order API
export interface ViolationOrderSimpleValue {
	id: string;
	licenseNumber: string;
	licenseState: string;
	violationPrediction: {
		orderMvr: string;
		orderDate: string; // ISO date string
	}
}

export interface ViolationOrderApiResponse {
	errors: string[];
	isSuccess: boolean;
	result: {
		data: ViolationOrderSimpleValue[];
	}
}

// Raw vehicle data interface for VehicleInformationList constructor
export interface RawVehicleData {
	insuredAssetNumber?: string | number;
	year?: string | number;
	vehicleDescription?: string;
	classCode?: string;
	vin?: string | number;
}

// MVR API Response Structure Interfaces for mapMvrResponseData function
export interface MvrApiName {
	first?: string;
	middle?: string;
	last?: string;
}

export interface MvrApiDriversLicense {
	licenseNumber?: string;
	licenseState?: string;
	licenseClass?: string;
	licenseStatus?: string;
	expireDate?: string;
}

export interface MvrApiRequestSubject {
	name?: MvrApiName;
	dob?: string;
	driversLicense?: MvrApiDriversLicense;
}

export interface MvrApiResponseSubject {
	gender?: string;
	driversLicenses?: MvrApiDriversLicense[];
}

export interface MvrApiProcessingInfo {
	productProcessingStatusDescription?: string;
}

export interface MvrApiResponseDetails {
	orderId?: string;
	status?: string;
	dateTimeOrdered?: string;
	dateTimeCompleted?: string;
}

export interface MvrApiStandardViolation {
	fullSVC?: string;
	primarySVC?: string;
	description?: string;
	pointsAssigned?: string;
	violationClassification?: string;
	convictionDate?: string;
}

export interface MvrApiDriverHistoryRecord {
	recordType?: string;
	violationDate?: string;
	effectiveDate?: string;
	accidentDate?: string;
	actionDate?: string;
	convictionDate?: string;
	actionEndDate?: string;
	reinstatementDate?: string;
	standardViolation?: MvrApiStandardViolation;
	type?: string;
	stateCode?: string;
	stateText?: string;
	nationalCode?: string;
	nationalText?: string;
	customerCode?: string;
	codeClass?: string;
	descriptions?: string[];
	points?: number;
	speedLimit?: number;
	speedDriven?: number;
	speedAmountOverLimit?: number;
	convictionCategoryText?: string;
	miscellaneousInfos?: MiscellaneousInfo[];
}

export interface MvrApiReport {
	responseSubject?: MvrApiResponseSubject;
	processingInfo?: MvrApiProcessingInfo;
	driverHistoryRecords?: MvrApiDriverHistoryRecord[];
}

export interface MvrApiMotorVehicleReport {
	responseDetails?: MvrApiResponseDetails;
	requestSubject?: MvrApiRequestSubject;
	mvrReports?: MvrApiReport[];
}

export interface MvrApiDataItem {
	motorVehicleReport?: MvrApiMotorVehicleReport;
}

export interface MvrOrderApiResponse {
	errors?: string[];
	isSuccess?: boolean;
	result?: {
		data?: MvrApiDataItem[];
	};
}

// Union type for API response flexibility
export type MvrApiResponseUnion = ViolationOrderApiResponse | MvrOrderApiResponse;

// Enhanced record type for normalized dates
export interface NormalizedMvrRecord {
	primaryDate: string;
	normalizedConvictionDate: string;
}
